import { CANCEL_EDITING_REQUEST } from '../../shared/actions/shared.actions'
import { IndexedCollection, PayloadedAction } from '../../shared/types'
import { VEHICLE_ADD, VEHICLE_UPDATE } from '../member/member.actions'
import { VehicleData } from '../member/member.types'
import {
  CLEAR_EDIT_METADATA,
  CREATE_NEW_VEHICLE,
  EDIT_VEHICLE,
  RESET_VEHICLE_BY_PLATE,
  SET_EDIT_METADATA,
  SET_IS_VEHICLE_EDITING,
  SET_VEHICLE_BY_PLATE,
  SET_VEHICLE_BY_PLATE_COLOR,
  SET_VEHICLE_DETAILS,
  SET_VEHICLE_MAKE,
  SET_VEHICLE_MODEL_AND_TYPE,
  SET_VEHICLE_STEP,
  SET_VEHICLE_YEAR,
  VEHICLE_BY_PLATE,
  VEHICLE_DETAILS,
  VEHICLE_MAKES,
  VEHICLE_MODELS,
  VEHICLE_YEARS,
} from './vehicle.actions'
import { Vehicle, VehicleDetails, VehicleDriveTypes, VehicleEditMetadata, VehicleType } from './vehicle.types'

export interface VehicleEditState {
  id?: string
  color?: string
  imageUrl?: string | null
  driveType?: VehicleDriveTypes
  make?: string
  model?: string
  notes?: string
  vin?: string
  vehicleType?: VehicleType
  wheelDrive?: string
  year?: number
}

export interface VehicleState {
  step: string
  selectedVehicle?: VehicleData
  years: string[]
  makes: string[]
  models: string[]
  colors: string[]
  workingVehicle: VehicleEditState
  isVehicleEditing: boolean
  editMetadata: VehicleEditMetadata
  vehiclesByPlate: Vehicle[]
  vehicleByPlate?: Vehicle
  vehicleDetails?: IndexedCollection<VehicleDetails[]>
}

export const initialState: VehicleState = {
  step: '',
  years: [],
  makes: [],
  models: [],
  colors: [],
  workingVehicle: {},
  isVehicleEditing:  false,
  editMetadata: {},
  vehiclesByPlate: null,
  vehicleDetails: {},
}

export function reducer(
  state: VehicleState | undefined = initialState,
  action: PayloadedAction
): VehicleState {
  switch (action.type) {
    case SET_VEHICLE_STEP:
      return {
        ...state,
        step: action.payload.step,
      }
    case VEHICLE_YEARS.SUCCESS: {
      return {
        ...state,
        years: [...(action.payload.years || [])],
      }
    }
    case VEHICLE_MAKES.SUCCESS: {
      return {
        ...state,
        makes: [...(action.payload.makes || [])],
      }
    }
    case VEHICLE_MODELS.REQUEST: {
      // Clear models, since they change depending on make.  If not done,
      // you can briefly see the wrong models after re-selecting a make.
      const result = { ...state }
      result.models = []
      return result
    }
    case VEHICLE_MODELS.SUCCESS: {
      return {
        ...state,
        models: [...(action.payload.models || [])],
      }
    }

    case EDIT_VEHICLE: {
      return {
        ...state,
        workingVehicle: { ...action.payload.vehicle },
      }
    }
    case SET_IS_VEHICLE_EDITING: {
      return {
        ...state,
        isVehicleEditing: action.payload
      }
    }
    case SET_EDIT_METADATA: {
      return {
        ...state,
        editMetadata: { ...action.payload },
      }
    }
    case CLEAR_EDIT_METADATA: {
      // Active (mid-edit) records shouldn't stomp the working vehicle!
      if (state.editMetadata.isActive) {
        return {
          ...state,
          editMetadata: {},
        }
      } else {
        return {
          ...state,
          editMetadata: {},
          workingVehicle: {},
        }
      }
    }
    case CREATE_NEW_VEHICLE: {
      const workingVehicle = {
        ...action.payload,
      }

      return {
        ...state,
        workingVehicle,
      }
    }
    case SET_VEHICLE_YEAR: {
      const workingVehicle = {
        ...state.workingVehicle,
        year: action.payload,
      }

      return {
        ...state,
        workingVehicle,
      }
    }
    case SET_VEHICLE_MAKE: {
      const workingVehicle = {
        ...state.workingVehicle,
        make: action.payload,
        model: '',
      }

      return {
        ...state,
        workingVehicle,
      }
    }

    case SET_VEHICLE_MODEL_AND_TYPE: {
      const workingVehicle = {
        ...state.workingVehicle,
        model: action.payload.model,
        driveType: action.payload.driveType,
        vehicleType: action.payload.vehicleType,
      }

      return {
        ...state,
        workingVehicle,
      }
    }

    case VEHICLE_ADD.SUCCESS:
    case VEHICLE_UPDATE.SUCCESS:
    case SET_VEHICLE_DETAILS: {
      const workingVehicle = {
        ...state.workingVehicle,
        ...action.payload,
      }

      return {
        ...state,
        workingVehicle,
        editMetadata: {
          ...state.editMetadata,
          completionUrl: null,
        },
      }
    }
    case CANCEL_EDITING_REQUEST:
      return {
        ...initialState,
        years: state.years,
        makes: state.makes,
        colors: state.colors,
      }
    case RESET_VEHICLE_BY_PLATE: {
      return {
        ...state,
        vehiclesByPlate: null,
      }
    }
    case VEHICLE_BY_PLATE.SUCCESS: {
      return {
        ...state,
        vehiclesByPlate: action.payload.vehicles,
      }
    }
    case SET_VEHICLE_BY_PLATE_COLOR: {
      return {
        ...state,
        vehiclesByPlate: state.vehiclesByPlate?.map((vehicle) => ({
          ...vehicle,
          color: action.payload.color,
        })),
      }
    }
    case SET_VEHICLE_BY_PLATE: {
      return {
        ...state,
        vehicleByPlate: action.payload,
      }
    }
    case VEHICLE_DETAILS.SUCCESS: {
      return {
        ...state,
        vehicleDetails: {
          ...state.vehicleDetails,
          ...action.payload,
        },
      }
    }
    default:
      return state
  }
}
